// import { observable } from "mobx"
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
// tslint:disable-next-line: no-submodule-imports
import { FaTrash } from 'react-icons/fa';
// import { Pill } from "src/ui/widgets/pill/pill"

import { makePaginated } from '../../../../adl-gen/common';
import { DbKey } from '../../../../adl-gen/common/db';
import { TrainingOffering, TrainingOfferingCategory } from '../../../../adl-gen/totalinteraction/futurefit/db';
import { TrainingOfferingData } from '../../../../adl-gen/totalinteraction/futurefit/frontend';
import { EditIcon } from '../../../../ui/common/icons';
import { Modal } from '../../../../ui/components/modal/modal';
import { Tile } from '../../../../ui/components/tile/tile';
import { Button } from '../../../../ui/widgets/button/button';
import { MarkdownContent } from '../../../../ui/widgets/markdown-content/markdown-content';
import { assertNever } from '../../../../util/types';
import { renderTemplate } from '../../../../util/utils';
import { DisplayImg, DisplayText } from '../../common/editor';
import { TrainingOfferingListEntry } from '../lists/training-offering-lists/training-offering-list-view';
import { ListView } from '../../common/list/list-view';
import { AssignedSkillsView } from '../lists/training-offering-lists/training-offering-to-skill-list-view';

const styles = require('../../common/details-view.css');
const buttonStyles = require('../../common/common.css');
const editorStyles = require('../../common/editor.css');

interface TrainingOfferingDetailViewProps {
  trainingOffering: TrainingOfferingListEntry;
  data: TrainingOfferingData;
  onUpdate(trainingOfferingId: DbKey<TrainingOffering>): Promise<void>;
  onLaunchTrainingOffering(trainingOffering: TrainingOfferingListEntry): string;
  role: 'client-admin' | 'super';
}

export class TrainingOfferingDetailView extends React.Component<TrainingOfferingDetailViewProps> {
  /// TODO: Sort this out later
  private readonly deleteAllowed: boolean = false;

  // Manage preview modal
  @observable isOpen: boolean = false;

  @action toggleOpen = () => {
    this.isOpen = !this.isOpen;
  };

  @computed
  get categoryToString(): string {
    const { trainingOffering } = this.props;
    switch (trainingOffering.category) {
      case TrainingOfferingCategory.elearning:
        return 'Self-Paced';
      case TrainingOfferingCategory.face2face:
        return 'Face to Face';
      case TrainingOfferingCategory.virtual:
        return 'Virtual Instructor-Led';
      default:
        assertNever(trainingOffering.category, 'Invalid Category');
        return '';
    }
  }

  @computed
  get catalogEntryCourseId(): string {
    const { data, trainingOffering } = this.props;
    const catEntry = data.catalog.find(entry => entry.id === trainingOffering.catalogEntryId);
    return catEntry ? catEntry.value.courseId : 'None';
  }

  @computed
  get providerName(): string {
    const { data, trainingOffering } = this.props;
    const provider = data.learningProviders.find(entry => entry.id === trainingOffering.providerId);
    return provider ? provider.value.name : 'notfound';
  }

  Title = DisplayText({
    field: this.props.trainingOffering.title,
    title: 'Title',
    xsSpan: 2
  });
  Duration = DisplayText({
    field: this.props.trainingOffering.duration,
    title: 'Duration',
    xsSpan: 2
  });
  ExecuteUrl = DisplayText({
    field: this.props.trainingOffering.executeUrl,
    title: 'Target URL',
    xsSpan: 2
  });
  TileUrl = DisplayImg({
    field: this.props.trainingOffering.tileUrl,
    title: 'Tile image',
    // height:
    xsSpan: 2
  });
  Category = DisplayText({
    field: this.categoryToString,
    title: 'Category',
    xsSpan: 2
  });
  CatalogEntry = DisplayText({
    field: this.catalogEntryCourseId,
    title: 'Catalog Entry',
    xsSpan: 2
  });
  Provider = DisplayText({
    field: this.providerName,
    title: 'Learning Provider',
    xsSpan: 2
  });

  SkillTrainingOfferingList = () => {
    const { availableSkills } = this.props.data;
    const assignedSkills = availableSkills
        .filter(sto => sto.value.trainingOfferingId === this.props.trainingOffering.id);
    return (assignedSkills.length > 0 ? <ListView
      list={makePaginated({
        items: assignedSkills.map(sk => ({ id: sk.id, ...sk.value })),
        current_offset: 0,
        total_size: assignedSkills.length
      })}
      fields={AssignedSkillsView}
      refData={this.props.data}
    /> : null);
}
  Detail = observer(() => {
    const { onLaunchTrainingOffering, trainingOffering } = this.props;
    const launchUrl = onLaunchTrainingOffering(trainingOffering);
    return (
      <Modal
        kind="editor"
        isOpen={this.isOpen}
        onRequestClose={this.toggleOpen}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        <div className={editorStyles.title}>{trainingOffering.title}</div>
        <div className={editorStyles.description}>
          <MarkdownContent
            text={renderTemplate(trainingOffering.description, {
              TrainingOffering: { ...trainingOffering, executeUrl: launchUrl }
            })}
          />
        </div>
      </Modal>
    );
  });

  Preview = observer(() => {
    /// Convert current input to I18nContent and use system language selector
    /// to display text
    const { trainingOffering } = this.props;
    return (
      <React.Fragment>
        <this.Detail />
        <Tile
          kind={'recommendation'}
          onClick={this.toggleOpen}
          title={trainingOffering.title}
          subtitle={this.categoryToString}
          enabled={true}
          completed={false}
          imageUrl={trainingOffering.tileUrl}
        />
      </React.Fragment>
    );
  });

  renderTopButtons = () => {
    const updateButton = () => {
      return (
        <div className={buttonStyles.buttonList}>
          <Button
            onClick={async () => this.props.onUpdate(this.props.trainingOffering.id)}
            Icon={EditIcon}
            size="small"
            variant="round"
            // disabled={this.editable() === undefined}
          >
            Update
          </Button>
        </div>
      );
    };

    const deleteButton = () => {
      return (
        <div className={buttonStyles.buttonList}>
          <Button
            Icon={FaTrash}
            size="small"
            variant="round"
          >
            Delete
          </Button>
        </div>
      );
    };

    return (
      <div className={buttonStyles.buttonListContainer}>
        {this.deleteAllowed && deleteButton()}
        {updateButton()}
      </div>
    );
  };

  renderDetails = () => (
    <div className={styles.page}>
      <div className={styles.body}>
        <div>{this.renderTopButtons()}</div>
        <div className={styles.row}>
          <div className={styles.detailWithList}>
            <this.Preview />
            <this.SkillTrainingOfferingList />
          </div>
        </div>
        <div className={styles.row}>
          {this.CatalogEntry}
        </div>
        <div className={styles.row}>
          {this.Provider}
        </div>
      </div>
    </div>
  );

  render() {
    return <React.Fragment>{this.renderDetails()}</React.Fragment>;
  }
}



// WEBPACK FOOTER //
// ./src/admin/ui/components/detail-views/training-offering-detail-view.tsx